// src/data/m2/Unit9.js

export const M2_UNIT_9 = {
  id: "m2-u9",
  title: "Unit 9: Describing Places",
  topic: "Adjectives (Before vs After Noun)",
  iconType: "star", 
  grammarImage: "/m2-u9-grammar.jpg", 
  grammarText: "Regla de Oro: Los adjetivos van **ANTES** del sustantivo ('A big house') o **DESPUÉS** del verbo 'to be' ('The house is big').",
  
  sets: [
    // ----------------------------------------------------------------------
    // SET 1: ADJECTIVE VOCABULARY (Opposites)
    // Objetivo: Vocabulario básico por pares.
    // ----------------------------------------------------------------------
    {
      id: "u25-set1",
      title: "Set 1: Opposites Match",
      questions: [
        { 
          type: "match", 
          q: "Empareja los opuestos:",
          pairs: [
            { left: "Big (Grande)", right: "Small (Pequeño)" },
            { left: "Old (Viejo)", right: "New (Nuevo)" },
            { left: "Noisy (Ruidoso)", right: "Quiet (Tranquilo)" },
            { left: "Beautiful (Hermoso)", right: "Ugly (Feo)" }
          ],
          a: "completed"
        },
        { 
          type: "mc", 
          q: "Opposite of 'Busy':", 
          options: ["Quiet", "Noisy", "Crowded"], 
          a: "Quiet",
          tip: "'Busy' puede ser 'ocupado' (persona) o 'concurrido' (lugar)."
        },
        { 
          type: "image_mc", 
          q: "¿Qué adjetivo describe mejor la imagen? (Basura en el suelo)", 
          options: ["Dirty", "Clean", "Beautiful"], 
          a: "Dirty" 
        },
        { 
          type: "blank", 
          q: "It's not expensive, it's ___ (barato).", 
          options: ["cheap"], 
          a: "cheap" 
        },
        { 
          type: "order", 
          q: "Ordena: Es una ciudad grande.", 
          words: ["It", "is", "a", "big", "city."], 
          a: "It is a big city." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 2: POSITION 1 - BEFORE NOUN
    // Objetivo: "A [Adjective] [Noun]"
    // ----------------------------------------------------------------------
    {
      id: "u25-set2",
      title: "Set 2: Before the Noun",
      questions: [
        { 
          type: "mc", 
          q: "She is a ___ woman.", 
          options: ["busy", "busys", "busying"], 
          a: "busy",
          tip: "Los adjetivos en inglés NO tienen plural. Nunca digas 'busys'."
        },
        { 
          type: "order", 
          q: "Ordena: Es un pueblo tranquilo.", 
          words: ["It", "is", "a", "quiet", "town."], 
          a: "It is a quiet town." 
        },
        { 
          type: "blank", 
          q: "They are ___ (pequeños) children.", 
          options: ["small"], 
          a: "small" 
        },
        { 
          type: "mc", 
          q: "I have ___ cars.", 
          options: ["two new", "new two"], 
          a: "two new",
          successTip: "Orden: Cantidad + Adjetivo + Sustantivo."
        },
        { 
          type: "order", 
          q: "Crea: Una casa hermosa.", 
          words: ["A", "beautiful", "house."], 
          a: "A beautiful house." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 3: POSITION 2 - AFTER VERB 'TO BE'
    // Objetivo: "The [Noun] is [Adjective]"
    // ----------------------------------------------------------------------
    {
      id: "u25-set3",
      title: "Set 3: After 'To Be'",
      questions: [
        { 
          type: "mc", 
          q: "The town ___ busy.", 
          options: ["is", "are"], 
          a: "is" 
        },
        { 
          type: "order", 
          q: "Ordena: La ciudad es vieja.", 
          words: ["The", "city", "is", "old."], 
          a: "The city is old." 
        },
        { 
          type: "blank", 
          q: "My neighbors ___ (son) noisy.", 
          options: ["are"], 
          a: "are" 
        },
        { 
          type: "mc", 
          q: "This car ___ expensive.", 
          options: ["is", "an"], 
          a: "is" 
        },
        { 
          type: "order", 
          q: "Ordena: Los árboles son verdes.", 
          words: ["The", "trees", "are", "green."], 
          a: "The trees are green." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 4: TRANSFORMATION CHALLENGE
    // Objetivo: Cambiar de estructura A -> B.
    // ----------------------------------------------------------------------
    {
      id: "u25-set4",
      title: "Set 4: Switch It Up",
      questions: [
        { 
          type: "mc", 
          q: "Transform: 'It is a big house.' -> 'The house is...'", 
          options: ["big", "a big"], 
          a: "big",
          tip: "Después de 'is', el adjetivo va solo (sin 'a' ni 'an')."
        },
        { 
          type: "order", 
          q: "Transform: 'He is a busy man.' ->", 
          words: ["The", "man", "is", "busy."], 
          a: "The man is busy." 
        },
        { 
          type: "mc", 
          q: "Transform: 'They are expensive shoes.' -> 'The shoes...'", 
          options: ["are expensive", "is expensive"], 
          a: "are expensive" 
        },
        { 
          type: "blank", 
          q: "'It is a quiet village.' -> The village is ___.", 
          options: ["quiet"], 
          a: "quiet" 
        },
        { 
          type: "order", 
          q: "Transform: 'She is a happy girl.' ->", 
          words: ["The", "girl", "is", "happy."], 
          a: "The girl is happy." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 5: THE HORROR HOTEL (Negative Adjectives)
    // Objetivo: Describir una mala experiencia.
    // ----------------------------------------------------------------------
    {
      id: "u25-set5",
      title: "Set 5: The Horror Hotel 👻",
      questions: [
        { 
          type: "mc", 
          q: "The hotel was ___ (muy malo).", 
          options: ["horrible", "great", "nice"], 
          a: "horrible" 
        },
        { 
          type: "blank", 
          q: "The room was ___ (sucio).", 
          options: ["dirty"], 
          a: "dirty" 
        },
        { 
          type: "match", 
          q: "Quejas del hotel:",
          pairs: [
            { left: "Bed", right: "Hard (Dura)" },
            { left: "Shower", right: "Cold (Fría)" },
            { left: "Food", right: "Bad (Mala)" }
          ],
          a: "completed" 
        },
        { 
          type: "order", 
          q: "Ordena: Los vecinos eran ruidosos.", 
          words: ["The", "neighbors", "were", "noisy."], 
          a: "The neighbors were noisy." 
        },
        { 
          type: "mc", 
          q: "It was a ___ experience.", 
          options: ["terrible", "terribles"], 
          a: "terrible" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 6: DREAM VACATION (Positive Adjectives)
    // Objetivo: Describir el paraíso.
    // ----------------------------------------------------------------------
    {
      id: "u25-set6",
      title: "Set 6: Dream Vacation 🌴",
      questions: [
        { 
          type: "mc", 
          q: "The beach is ___.", 
          options: ["beautiful", "ugly"], 
          a: "beautiful" 
        },
        { 
          type: "blank", 
          q: "The weather is ___ (perfecto).", 
          options: ["perfect"], 
          a: "perfect" 
        },
        { 
          type: "match", 
          q: "Descripciones positivas:",
          pairs: [
            { left: "Water", right: "Clean & Blue" },
            { left: "People", right: "Friendly (Amigables)" },
            { left: "Food", right: "Delicious" }
          ],
          a: "completed" 
        },
        { 
          type: "order", 
          q: "Ordena: Es un lugar seguro.", 
          words: ["It", "is", "a", "safe", "place."], 
          a: "It is a safe place." 
        },
        { 
          type: "input", 
          q: "Escribe: 'Relajante' (en inglés - R...)", 
          a: "relaxing" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 7: DESCRIBING PEOPLE (Character)
    // Objetivo: Adjetivos de personalidad.
    // ----------------------------------------------------------------------
    {
      id: "u25-set7",
      title: "Set 7: Describing People",
      questions: [
        { 
          type: "mc", 
          q: "My grandfather works a lot. He is ___.", 
          options: ["hardworking", "lazy"], 
          a: "hardworking" 
        },
        { 
          type: "blank", 
          q: "She always smiles. She is ___ (feliz).", 
          options: ["happy"], 
          a: "happy" 
        },
        { 
          type: "match", 
          q: "Personalidad:",
          pairs: [
            { left: "Nice", right: "Simpático/Agradable" },
            { left: "Kind", right: "Amable" },
            { left: "Smart", right: "Inteligente" },
            { left: "Funny", right: "Divertido" }
          ],
          a: "completed" 
        },
        { 
          type: "order", 
          q: "Traduce: Él es un hombre tranquilo.", 
          words: ["He", "is", "a", "quiet", "man."], 
          a: "He is a quiet man." 
        },
        { 
          type: "input", 
          q: "Escribe el opuesto de 'Lazy' (Vago):", 
          a: "hardworking" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 8: QUANTITY PHRASES (A few / Lots of)
    // Objetivo: Adjetivos de cantidad.
    // ----------------------------------------------------------------------
    {
      id: "u25-set8",
      title: "Set 8: How many?",
      questions: [
        { 
          type: "mc", 
          q: "There are ___ people. (Muchos)", 
          options: ["lots of", "a few"], 
          a: "lots of" 
        },
        { 
          type: "blank", 
          q: "There are ___ (pocos) cars.", 
          options: ["a few"], 
          a: "a few" 
        },
        { 
          type: "match", 
          q: "Cantidades:",
          pairs: [
            { left: "A few", right: "Small number (3-4)" },
            { left: "Some", right: "Medium number" },
            { left: "Lots of", right: "Big number" }
          ],
          a: "completed" 
        },
        { 
          type: "order", 
          q: "Ordena: Hay muchos edificios.", 
          words: ["There", "are", "lots", "of", "buildings."], 
          a: "There are lots of buildings." 
        },
        { 
          type: "mc", 
          q: "I have ___ friends here. (2 or 3)", 
          options: ["a few", "lots of"], 
          a: "a few" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 9: ERROR CORRECTION (Grammar Police)
    // Objetivo: Errores comunes (plurales en adjetivos, orden).
    // ----------------------------------------------------------------------
    {
      id: "u25-set9",
      title: "Set 9: Find the Mistake",
      questions: [
        { 
          type: "mc", 
          q: "Error: 'They are news cars.'", 
          options: ["Fix: NEW cars", "Fix: A new cars"], 
          a: "Fix: NEW cars",
          tip: "¡Nunca pongas 's' al adjetivo! Solo al sustantivo."
        },
        { 
          type: "order", 
          q: "Corrige: 'It is a house big.'", 
          words: ["It", "is", "a", "big", "house."], 
          a: "It is a big house." 
        },
        { 
          type: "mc", 
          q: "Error: 'The people is friendly.'", 
          options: ["Fix: The people ARE", "Fix: The people AM"], 
          a: "Fix: The people ARE",
          tip: "'People' siempre es plural."
        },
        { 
          type: "input", 
          q: "Corrige: 'She is a girl happy.' -> She is a...", 
          a: "happy girl" 
        },
        { 
          type: "mc", 
          q: "Error: 'Is a beautiful day.' (Falta sujeto)", 
          options: ["Fix: IT IS a...", "Fix: HE IS a..."], 
          a: "Fix: IT IS a..." 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 10: MASTER CHALLENGE (Storytelling)
    // Objetivo: Mezcla total.
    // ----------------------------------------------------------------------
    {
      id: "u25-set10",
      title: "Set 10: The Storyteller",
      questions: [
        { 
          type: "mc", 
          q: "STORY: I live in a ___ village.", 
          options: ["quiet", "quiets"], 
          a: "quiet" 
        },
        { 
          type: "blank", 
          q: "The streets are ___ (limpias).", 
          options: ["clean"], 
          a: "clean" 
        },
        { 
          type: "mc", 
          q: "But the internet is ___ (lento).", 
          options: ["slow", "fast"], 
          a: "slow" 
        },
        { 
          type: "order", 
          q: "Describe a tu abuelo: Es viejo pero fuerte.", 
          words: ["He", "is", "old,", "but", "strong."], 
          a: "He is old, but strong." 
        },
        { 
          type: "match", 
          q: "Resumen:",
          pairs: [
            { left: "Village", right: "Quiet & Clean" },
            { left: "Internet", right: "Slow" },
            { left: "Neighbors", right: "Friendly" }
          ],
          a: "completed" 
        }
      ]
    }
  ]
};